import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Icon } from "@iconify/react";
import { Input } from "antd";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { RequiredMark } from "@/shared/components/RequiredMark";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { validateFields } from "@/shared/lib/validators";
import { cn } from "@/shared/lib/utils";
import { sendContactMessageApi, type ContactMessage } from "../api/contactApi";
import { CONTACT_EMAIL } from "./primitives";

type Source = ContactMessage["source"];

const COPY: Record<Source, { title: string; subject: string; placeholder: string }> = {
  demo: {
    title: "Book a Demo",
    subject: "MedSyncIQ demo request",
    placeholder: "Tell us about your organization and what you'd like to see.",
  },
  contact: {
    title: "Contact Us",
    subject: "",
    placeholder: "How can we help?",
  },
};

const ContactContext = createContext<(source: Source) => void>(() => {});

/** Opens the landing page's contact popup ("Book a Demo", "Contact"). */
export const useOpenContact = () => useContext(ContactContext);

/** Mount once around the landing page; every button shares the one popup. */
export function ContactProvider({ children }: { children: ReactNode }) {
  const [source, setSource] = useState<Source | null>(null);
  // Bumped on every opening, so each one starts with a fresh form while
  // closing keeps the same instance (and antd's close animation).
  const [opening, setOpening] = useState(0);
  const open = useCallback((next: Source) => {
    setSource(next);
    setOpening((n) => n + 1);
  }, []);
  return (
    <ContactContext.Provider value={open}>
      {children}
      <ContactModal key={opening} source={source} onClose={() => setSource(null)} />
    </ContactContext.Provider>
  );
}

const SHELL =
  "msq-field-shell rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-3 [&_:focus-visible]:!outline-none";
const INPUT = "!w-full !bg-transparent !p-0 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF]";

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold text-[#374151]">
        {label}
        <RequiredMark />
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-[#E4002B]">{error}</p>}
    </div>
  );
}

function ContactModal({ source, onClose }: { source: Source | null; onClose: () => void }) {
  // The last opened kind stays on screen while the popup animates closed.
  const [shown] = useState<Source>(source ?? "contact");
  const copy = COPY[shown];
  const [values, setValues] = useState({ name: "", email: "", subject: copy.subject, message: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof typeof values, string>>>({});
  const [sending, setSending] = useState(false);

  const set = (field: keyof typeof values) => (event: { target: { value: string } }) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const close = () => {
    if (!sending) onClose();
  };

  const send = async () => {
    const trimmed = {
      name: values.name.trim(),
      email: values.email.trim(),
      subject: values.subject.trim(),
      message: values.message.trim(),
    };
    const fieldErrors = validateFields(trimmed, {
      name: [{ required: true, message: "Please enter your name" }],
      email: [
        { required: true, message: "Please enter your email" },
        { type: "email", message: "Enter a valid email address" },
      ],
      subject: [{ required: true, message: "Please add a subject" }],
      message: [{ required: true, message: "Please write a message" }],
    });
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setSending(true);
    const { error } = await sendContactMessageApi({ ...trimmed, source: shown });
    setSending(false);
    if (error) {
      toastError(error);
      return;
    }
    toastSuccess("Message sent. We'll get back to you soon.");
    onClose();
  };

  return (
    <AppModal
      open={source !== null}
      onOpenChange={(next) => { if (!next) close(); }}
      centered
      width={560}
      title={<span className="text-lg font-extrabold text-[#0A2947]">{copy.title}</span>}
      description={
        <span className="flex items-center gap-1.5 text-sm text-[#6B7280]">
          <Icon icon="lucide:mail" className="h-4 w-4 shrink-0" />
          To: <span className="font-semibold text-[#0A2947]">{CONTACT_EMAIL}</span>
        </span>
      }
      footer={
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={close}
            disabled={sending}
            className="!h-10 !rounded-lg !border-[#E5E7EB] !px-5 !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E5E7EB] hover:!bg-[#F9FAFB] hover:!text-[#0A2947]"
          >
            Cancel
          </Button>
          <Button
            onClick={() => void send()}
            loading={sending}
            className="!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-5 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
          >
            <Icon icon="lucide:send" className="h-4 w-4" />
            Send
          </Button>
        </div>
      }
    >
      <div className="mt-5 grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="contact-name" label="Your name" error={errors.name}>
            <div className={cn(SHELL, "flex h-[42px] items-center")}>
              <Input id="contact-name" variant="borderless" value={values.name} onChange={set("name")} placeholder="Jane Doe" disabled={sending} className={INPUT} />
            </div>
          </Field>
          <Field id="contact-email" label="Your email" error={errors.email}>
            <div className={cn(SHELL, "flex h-[42px] items-center")}>
              <Input id="contact-email" variant="borderless" type="email" value={values.email} onChange={set("email")} placeholder="name@example.com" disabled={sending} className={INPUT} />
            </div>
          </Field>
        </div>
        <Field id="contact-subject" label="Subject" error={errors.subject}>
          <div className={cn(SHELL, "flex h-[42px] items-center")}>
            <Input id="contact-subject" variant="borderless" value={values.subject} onChange={set("subject")} placeholder="What's this about?" disabled={sending} className={INPUT} />
          </div>
        </Field>
        <Field id="contact-message" label="Message" error={errors.message}>
          <div className={cn(SHELL, "py-2")}>
            <Input.TextArea
              id="contact-message"
              variant="borderless"
              value={values.message}
              onChange={set("message")}
              placeholder={copy.placeholder}
              autoSize={{ minRows: 5, maxRows: 10 }}
              disabled={sending}
              className={cn(INPUT, "!resize-none")}
            />
          </div>
        </Field>
      </div>
    </AppModal>
  );
}
